<template>
  <div class="mt-inicio">
    <!-- Portada: lo primero que se ve es cómo empezar -->
    <section class="mt-portada">
      <div class="mt-contenedor mt-portada__rejilla">
        <div class="mt-portada__texto">
          <p class="mt-etiqueta"><v-icon size="16">mdi-check-decagram</v-icon> Una red de más de {{ RED_NOTARIAS }} notarías en toda España</p>
          <h1>Empiece hoy su trámite notarial, sin salir de casa</h1>
          <p class="mt-portada__sub">
            Cuéntenos qué necesita, elija notaría y envíe sus datos y documentos en unos minutos.
            La notaría le contacta para prepararlo todo. Y si su trámite lo permite, puede firmar por videoconferencia.
          </p>
          <ul class="mt-portada__claves">
            <li><v-icon size="18">mdi-account-off-outline</v-icon> Sin registrarse</li>
            <li><v-icon size="18">mdi-gift-outline</v-icon> Sin coste por usar el servicio</li>
            <li><v-icon size="18">mdi-hand-pointing-right</v-icon> Usted elige la notaría</li>
            <li><v-icon size="18">mdi-video-outline</v-icon> Firma por videoconferencia en poderes, sociedades y actas</li>
          </ul>
        </div>

        <!-- Arranque rápido: trámite y provincia; el resto, en el formulario -->
        <form class="mt-arranque" @submit.prevent="empezar()">
          <p class="mt-arranque__titulo">¿Qué necesita?</p>
          <div class="mt-arranque__tramites" role="radiogroup" aria-label="Trámite">
            <button v-for="op in principales" :key="op.codigo" type="button" role="radio"
              class="mt-chip" :class="{ activo: tramite === op.codigo }" :aria-checked="tramite === op.codigo"
              @click="tramite = op.codigo">
              <v-icon size="18">{{ op.icono }}</v-icon>{{ op.nombre }}
              <v-icon v-if="op.admiteVideoconferencia" size="16" class="mt-chip__video" :aria-label="'También por videoconferencia'">mdi-video-outline</v-icon>
            </button>
          </div>
          <v-select v-if="resto.length" v-model="otroTramite" :items="resto" item-title="nombre" item-value="codigo"
            label="Otro trámite" variant="outlined" density="comfortable" hide-details class="mb-4" />
          <!-- Con videoconferencia no hace falta provincia: se firma con cualquier notaría que la ofrezca. Ref: https://trello.com/c/pooUEbKK -->
          <p v-if="tramiteConVideo" class="mt-arranque__video">
            <v-icon size="20">mdi-video-outline</v-icon>
            <span>Este trámite se puede firmar por videoconferencia, sin desplazarse.</span>
          </p>
          <v-autocomplete v-else v-model="provincia" :items="PROVINCIAS" item-title="nombre" item-value="codigo"
            label="¿Dónde quiere firmar?" variant="outlined" density="comfortable" hide-details
            no-data-text="Sin resultados" class="mb-4" />
          <button type="submit" class="mt-boton mt-boton--acento mt-boton--grande mt-arranque__boton">
            Empezar mi encargo <v-icon size="20">mdi-arrow-right</v-icon>
          </button>
          <p class="mt-arranque__nota"><v-icon size="16">mdi-timer-outline</v-icon> Unos 5 minutos. Puede dejarlo y seguir luego en este dispositivo.</p>
        </form>
      </div>
    </section>

    <!-- Confianza -->
    <section class="mt-confianza" aria-label="Garantías">
      <div class="mt-contenedor mt-confianza__fila">
        <div v-for="g in garantias" :key="g.titulo" class="mt-confianza__item">
          <v-icon size="28">{{ g.icono }}</v-icon>
          <div><strong>{{ g.titulo }}</strong><span>{{ g.texto }}</span></div>
        </div>
      </div>
    </section>

    <!-- La red: más notarías que las adheridas, con las que se gestionan los "sin preferencia" -->
    <section class="mt-red" aria-label="Red de notarías">
      <div class="mt-contenedor mt-red__rejilla">
        <div>
          <!-- Contador de 0 a la cifra al verse la franja; el lector de pantalla lee solo la cifra final.
               div y no p: el tema general fuerza 16px en los <p> en móvil -->
          <div ref="cifraRed" class="mt-red__cifra"><span aria-hidden="true">+{{ cifraMostrada }}</span><span class="mt-oculto">Más de {{ RED_NOTARIAS }}</span></div>
          <div class="mt-red__texto"><strong>notarías</strong> con las que gestionamos su encargo en toda España</div>
          <p class="mt-red__sub">De la península a Baleares y Canarias: elija una cerca de usted o deje que le asignemos una.</p>
          <NuxtLink :to="rutaTramites.replace('/tramites', '/notarias')" class="mt-red__enlace">
            Ver notarías por provincia <v-icon size="18">mdi-arrow-right</v-icon>
          </NuxtLink>
        </div>
        <!-- Mapa ilustrativo: los pines se reparten según la población de cada provincia, no son ubicaciones reales.
             Contorno de Natural Earth (dominio público), generado como SVG estático para no cargar la portada. Ref: https://trello.com/c/2DKePjFr -->
        <!-- Animado: los pines caen al ritmo del contador. Sin animación (servidor, "reducir movimiento"), la imagen completa -->
        <svg v-if="animarMapa" class="mt-red__mapa" :viewBox="`0 0 ${MAPA_ANCHO} ${MAPA_ALTO}`" role="img"
          :aria-label="`Mapa de España con más de ${RED_NOTARIAS} notarías`">
          <defs>
            <symbol id="mt-pin" viewBox="0 0 32 32">
              <rect x="1" y="1" width="30" height="30" rx="8" fill="#f08a3c" stroke="#123f5a" stroke-width="2" />
              <path d="M9 22V11.5l7 6 7-6V22" fill="none" stroke="#fff" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" />
            </symbol>
          </defs>
          <image href="/minotaria-mapa-fondo.svg" :width="MAPA_ANCHO" :height="MAPA_ALTO" />
          <use v-for="([x, y], i) in pinesVisibles" :key="i" href="#mt-pin" class="mt-pin"
            :x="x" :y="y" :width="PIN_TAMANO" :height="PIN_TAMANO" />
        </svg>
        <img v-else class="mt-red__mapa" src="/minotaria-mapa.svg" width="648" height="649" loading="lazy"
          :alt="`Mapa de España con más de ${RED_NOTARIAS} notarías`" />
      </div>
    </section>

    <!-- Cómo funciona -->
    <section id="como-funciona" class="mt-seccion">
      <div class="mt-contenedor">
        <h2 class="mt-seccion__titulo">Cómo funciona</h2>
        <p class="mt-seccion__sub">Tres pasos y la notaría se encarga del resto.</p>
        <ol class="mt-pasos">
          <li v-for="(p, i) in pasos" :key="p.titulo" class="mt-paso">
            <span class="mt-paso__num">{{ i + 1 }}</span>
            <v-icon class="mt-paso__icono" size="32">{{ p.icono }}</v-icon>
            <h3>{{ p.titulo }}</h3>
            <p>{{ p.texto }}</p>
          </li>
        </ol>
      </div>
    </section>

    <!-- Trámites -->
    <section id="tramites" class="mt-seccion mt-seccion--suave">
      <div class="mt-contenedor">
        <h2 class="mt-seccion__titulo">¿Qué trámite necesita?</h2>
        <p class="mt-seccion__sub">Elija uno y empiece. Le diremos qué documentos tener a mano.</p>
        <div class="mt-tramites">
          <button v-for="op in lista" :key="op.codigo" type="button" class="mt-tramite" @click="empezar(op.codigo)">
            <v-icon class="mt-tramite__icono" size="30">{{ op.icono }}</v-icon>
            <span class="mt-tramite__nombre">{{ op.nombre }}</span>
            <span v-if="op.admiteVideoconferencia" class="mt-tramite__video"><v-icon size="16">mdi-video-outline</v-icon> También por videoconferencia</span>
            <span class="mt-tramite__texto">{{ op.descripcion }}</span>
            <span class="mt-tramite__ir">Empezar <v-icon size="16">mdi-arrow-right</v-icon></span>
          </button>
        </div>
        <!-- Para quien prefiere informarse antes de empezar -->
        <div class="mt-guias">
          <p class="mt-guias__titulo">¿Prefiere informarse antes?</p>
          <div class="mt-guias__fila">
            <NuxtLink :to="rutaTramites" class="mt-guia">
              <v-icon class="mt-guia__icono" size="28">mdi-book-open-variant-outline</v-icon>
              <span class="mt-guia__cuerpo">
                <strong>Guías de trámites</strong>
                <span>Qué es cada trámite, qué documentos necesita y cómo se firma.</span>
              </span>
              <v-icon class="mt-guia__ir" size="20">mdi-arrow-right</v-icon>
            </NuxtLink>
            <NuxtLink :to="rutaTramites.replace('/tramites', '/notarias')" class="mt-guia">
              <img class="mt-guia__icono mt-guia__mapa" src="/minotaria-mapa-mini.svg" width="48" height="48" alt="" loading="lazy" />
              <span class="mt-guia__cuerpo">
                <strong>Notarías por provincia</strong>
                <span>Vea con qué notarías puede hacer su encargo en su zona.</span>
              </span>
              <v-icon class="mt-guia__ir" size="20">mdi-arrow-right</v-icon>
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Preguntas frecuentes: las del panel (Inicio → Preguntas frecuentes); si aún no hay ninguna, las de reserva. Ref: https://trello.com/c/2DKePjFr -->
    <section v-if="faqVisible" id="preguntas" class="mt-seccion">
      <div class="mt-contenedor mt-contenedor--estrecho">
        <h2 class="mt-seccion__titulo" v-html="faqTitulo"></h2>
        <div v-if="faqDescripcion" class="mt-seccion__sub" v-html="faqDescripcion"></div>
        <div class="mt-faq">
          <template v-if="faqPanel.length">
            <details v-for="(f, i) in faqPanel" :key="i" class="mt-faq__item">
              <summary><span v-html="f.Pregunta"></span><v-icon size="22">mdi-chevron-down</v-icon></summary>
              <div class="mt-faq__respuesta" v-html="f.Respuesta"></div>
            </details>
          </template>
          <template v-else>
            <details v-for="f in preguntas" :key="f.p" class="mt-faq__item">
              <summary>{{ f.p }}<v-icon size="22">mdi-chevron-down</v-icon></summary>
              <p>{{ f.r }}</p>
            </details>
          </template>
        </div>
      </div>
    </section>

    <!-- Blog (si está activo): las entradas marcadas "Mostrar en Home" o, si no hay, las últimas -->
    <section v-if="blogActivo && destacadas.length" id="blog" class="mt-seccion mt-seccion--suave">
      <div class="mt-contenedor">
        <h2 class="mt-seccion__titulo">Del blog</h2>
        <p class="mt-seccion__sub">Guías sencillas para llegar a la notaría con todo claro.</p>
        <div class="mt-entradas">
          <MinotariaBlogTarjeta v-for="e in destacadas" :key="e.id" :entrada="e" />
        </div>
        <div class="mt-blog__mas">
          <NuxtLink :to="rutaBlog" class="mt-boton mt-boton--borde">Ver todos los artículos</NuxtLink>
        </div>
      </div>
    </section>

    <!-- Cierre -->
    <section class="mt-cierre">
      <div class="mt-contenedor mt-cierre__fila">
        <div>
          <h2>¿Empezamos?</h2>
          <p>Cuéntenos qué necesita en unos minutos. Nosotros nos ocupamos de que llegue a la notaría adecuada.</p>
        </div>
        <button type="button" class="mt-boton mt-boton--acento mt-boton--grande" @click="empezar()">
          Empezar mi encargo <v-icon size="20">mdi-arrow-right</v-icon>
        </button>
      </div>
    </section>

    <!-- Para notarías: adhesión al portal. Ref: https://trello.com/c/8j6VOWgP -->
    <section id="notarias" class="mt-seccion mt-seccion--suave mt-adhesion">
      <div class="mt-contenedor mt-adhesion__rejilla">
        <div>
          <p class="mt-etiqueta"><v-icon size="16">mdi-bank-outline</v-icon> Para notarías</p>
          <h2 class="mt-adhesion__titulo">¿Es usted notaría? Únase a minotaria</h2>
          <p class="mt-adhesion__sub">
            Reciba encargos de particulares de su zona con los datos y la documentación ya preparados,
            listos para revisar y dar cita para la firma.
          </p>
          <ul class="mt-adhesion__ventajas">
            <li v-for="v in ventajasNotaria" :key="v.titulo">
              <v-icon size="24">{{ v.icono }}</v-icon>
              <div><strong>{{ v.titulo }}</strong><span>{{ v.texto }}</span></div>
            </li>
          </ul>
        </div>
        <MinotariaAdhesion />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { useSolicitudWeb } from '../../composables/useSolicitudWeb'
import { CATALOGO_TIPOS } from '../../data/solicitudWebCatalogo'
import { PROVINCIAS } from '../../data/provincias'
import { useStore } from '../../store/index'
import { useMinotariaBlog } from '../../composables/useMinotariaBlog'
import { RED_NOTARIAS } from '../../plantillas/minotaria'
import { PINES_MAPA, MAPA_ANCHO, MAPA_ALTO, PIN_TAMANO } from '../../data/minotariaPines'
import MinotariaBlogTarjeta from './MinotariaBlogTarjeta.vue'
import MinotariaAdhesion from './MinotariaAdhesion.vue'

// Portada de minotaria.es. Textos provisionales hasta que Marketing cierre los suyos.
const { tipos, cargarCatalogo } = useSolicitudWeb()
// El layout pinta esta portada en lugar de pages/index.vue: título y descripción SEO de "Diseño páginas → Inicio".
// Sin useMetaTags, que añade los datos estructurados de una notaría (dirección, notarios) y no valen para un agregador.
const store = useStore()
const seoInicio = store.NOTARIA?.Theme?.Home
if (store.NOTARIA) {
  const titulo = seoInicio?.Titulo?.Texto && seoInicio.Titulo.Texto !== 'Inicio' ? seoInicio.Titulo.Texto : ''
  const descripcion = seoInicio?.Description || ''
  // En la pestaña de la portada, solo el nombre de la web ("minotaria"); el título del panel queda para las redes
  useSeoMeta({
    description: descripcion || undefined,
    keywords: seoInicio?.Keywords || undefined,
    ogTitle: titulo ? `${titulo} | ${store.NOTARIA.Nombre}` : store.NOTARIA.Nombre,
    ogDescription: descripcion || undefined,
    ogType: 'website',
    robots: 'index, follow',
  })
  useHead({ link: [{ rel: 'canonical', href: `https://${store.host}` }] })
}
const router = useRouter()
const { activo: blogActivo, destacadas } = useMinotariaBlog()
const rutaTramites = computed(() => (router.currentRoute.value.params.lang ? `/${router.currentRoute.value.params.lang}` : '') + '/tramites')
const rutaBlog = computed(() => (router.currentRoute.value.params.lang ? `/${router.currentRoute.value.params.lang}` : '') + '/blog')

// En el servidor se pinta con el catálogo base (para buscadores); en el navegador, con el de la web
const lista = computed(() => (tipos.value.length ? tipos.value : CATALOGO_TIPOS))
const ORDEN = ['HERENCIA', 'COMPRAVENTA', 'TESTAMENTO', 'PODER', 'HIPOTECA']
const principales = computed(() => ORDEN.map((c) => lista.value.find((t) => t.codigo === c)).filter(Boolean) as typeof lista.value)
const resto = computed(() => lista.value.filter((t) => !ORDEN.includes(t.codigo)))

const tramite = useState<string | null>('minotariaTramiteInicio', () => null)
// La misma provincia que usa el paso "Notaría" del formulario: así ya aparece elegida
const provincia = useState<string | null>('minotariaProvincia', () => null)
// El desplegable solo muestra los trámites que no tienen botón (si no, saldría el código, p. ej. "PODER")
const otroTramite = computed<string | null>({
  get: () => (resto.value.some((t) => t.codigo === tramite.value) ? tramite.value : null),
  set: (v) => { tramite.value = v },
})
const tramiteConVideo = computed(() => !!lista.value.find((t) => t.codigo === tramite.value)?.admiteVideoconferencia)

onMounted(() => cargarCatalogo())

// Contador de la red: en el servidor (y sin animaciones) se pinta la cifra final; en el navegador cuenta desde 0 al verse
// y, a la vez, van cayendo los pines del mapa (tantos como marque el contador)
const cifraRed = ref<HTMLElement | null>(null)
const cifraMostrada = ref(RED_NOTARIAS)
const animarMapa = ref(false)
const pinesVisibles = computed(() => PINES_MAPA.slice(0, Math.round(PINES_MAPA.length * cifraMostrada.value / RED_NOTARIAS)))
let observador: IntersectionObserver | null = null
let animacion = 0
onMounted(() => {
  const sinMovimiento = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  if (sinMovimiento || !cifraRed.value || !('IntersectionObserver' in window)) return
  cifraMostrada.value = 0
  animarMapa.value = true
  observador = new IntersectionObserver((entradas) => {
    if (!entradas.some((e) => e.isIntersecting)) return
    observador?.disconnect()
    const inicio = performance.now(), duracion = 2400
    const paso = (ahora: number) => {
      const t = Math.min(1, (ahora - inicio) / duracion)
      cifraMostrada.value = Math.round(RED_NOTARIAS * (1 - Math.pow(1 - t, 3)))
      if (t < 1) animacion = requestAnimationFrame(paso)
    }
    animacion = requestAnimationFrame(paso)
  }, { threshold: 0.4 })
  observador.observe(cifraRed.value)
})
onBeforeUnmount(() => { observador?.disconnect(); cancelAnimationFrame(animacion) })

function empezar(codigo?: string) {
  const elegido = codigo ?? tramite.value
  const lang = router.currentRoute.value.params.lang
  router.push({ path: (lang ? `/${lang}` : '') + '/encargo-online', query: elegido ? { tramite: elegido } : {} })
}

const garantias = [
  { icono: 'mdi-bank-outline', titulo: 'Notarías adheridas', texto: 'Solo notarías que han aceptado las condiciones del servicio.' },
  { icono: 'mdi-shield-lock-outline', titulo: 'Datos protegidos', texto: 'Solo los recibe la notaría que lleva su encargo.' },
  { icono: 'mdi-file-document-check-outline', titulo: 'Documentos en orden', texto: 'Le decimos qué aportar según el trámite.' },
  { icono: 'mdi-account-voice', titulo: 'Atención personal', texto: 'La notaría le llama para preparar la firma.' },
  // Ref: https://trello.com/c/quriKGDT
  { icono: 'mdi-calendar-check-outline', titulo: 'Vea cuándo puede firmar antes de elegir', texto: 'Muchas notarías le muestran su próxima fecha disponible para firmar. Compare y elija la que mejor le encaje, sin tener que llamar a ninguna.' },
]

const pasos = [
  { icono: 'mdi-clipboard-text-outline', titulo: 'Cuéntenos qué necesita', texto: 'Elija el trámite y responda unas preguntas sencillas. Sin registrarse.' },
  { icono: 'mdi-map-marker-radius-outline', titulo: 'Elija notaría', texto: 'Elija una notaría de su zona o, si su trámite lo permite, cualquiera que firme por videoconferencia. Si no tiene preferencia, le asignamos una.' },
  { icono: 'mdi-draw-pen', titulo: 'La notaría le contacta', texto: 'Revisa sus datos y documentos, le informa del coste y acuerda con usted la firma, en la notaría o por videoconferencia.' },
]

const ventajasNotaria = [
  { icono: 'mdi-file-document-check-outline', titulo: 'Encargos ya preparados', texto: 'Datos del cliente, operación y documentos antes de la primera llamada.' },
  { icono: 'mdi-tune-variant', titulo: 'Usted decide', texto: 'Elija las operaciones y la zona que atiende, y pause cuando lo necesite.' },
  { icono: 'mdi-swap-horizontal', titulo: 'Sin cambiar su forma de trabajar', texto: 'Los encargos llegan como los de su propia web, marcados como de minotaria.' },
]

// Preguntas del panel, en su orden; "Mostrar" apagado en el panel oculta la sección
const faqConfig = computed(() => (store.NOTARIA as any)?.Theme?.Home?.FAQ)
const faqPanel = computed(() => ((store.NOTARIA as any)?.FAQs ?? [])
  .filter((f: any) => f?.Pregunta && f?.Respuesta)
  .sort((a: any, b: any) => (a.Orden ?? 0) - (b.Orden ?? 0)))
const faqVisible = computed(() => faqConfig.value?.Mostrar !== false)
const faqTitulo = computed(() => faqConfig.value?.Titulo?.Texto || 'Preguntas frecuentes')
const faqDescripcion = computed(() => faqConfig.value?.Descripcion?.Texto || '')

// Reserva mientras no haya preguntas en el panel
const preguntas = [
  { p: '¿Cuánto cuesta?', r: 'Usar minotaria no tiene coste. Los honorarios de la notaría están regulados por arancel; la notaría le informará antes de empezar y, si lo pide, le enviará un presupuesto.' },
  { p: '¿Puedo elegir la notaría?', r: 'Sí. Le mostramos las notarías adheridas de su zona y usted elige. Si no tiene preferencia, le asignamos una y se lo comunicamos por email.' },
  { p: '¿Tengo que ir a la notaría?', r: 'Depende del trámite. Poderes, trámites de sociedades y actas pueden firmarse en muchos casos por videoconferencia. El resto se firma en la notaría, y todo lo anterior (datos, documentos y dudas) se prepara a distancia.' },
  { p: '¿Cómo es la firma por videoconferencia?', r: 'Se hace a través del portal oficial del notariado. La notaría le confirma si su caso lo permite y le explica los pasos.' },
  { p: '¿Cuándo me contactan?', r: 'Si ha elegido notaría, ella le contacta directamente. Si no, le asignamos una y le avisamos en un máximo de 48 horas.' },
  { p: '¿Qué pasa con mis datos?', r: 'Sus datos y documentos se envían de forma segura solo a la notaría que lleva su encargo, que es la responsable de tratarlos. Puede consultar los detalles en Protección de datos.' },
  { p: '¿Y si no hay notaría en mi zona?', r: `Envíe igualmente su encargo. Trabajamos con una red de más de ${RED_NOTARIAS} notarías en toda España y buscamos una que pueda atenderle; si no la encontramos, se lo diremos.` },
]
</script>
